/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import {
	Card,
	CircularProgress,
	Drawer,
	Icon,
	IconButton,
	Tab,
	Tabs,
	Tooltip
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { useEffect, useState } from "react";
import FormAction from "redux-react/actions/formAction";
import { useDispatch } from "react-redux";
import Analyse from "./Analyse";
import { queryDictionary } from "helpers/dictionary";
import { createDictionarySkeleton } from "helpers/form";
import ConversationTab from "./ConversationTab";

const drawerStyles = fullScreen => ({
	"& .MuiDrawer-paper": {
		width: fullScreen ? "100vw" : "40vw",
		height: "100vh",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		margin: 0,
		boxSizing: "border-box",
		backgroundColor: "#fff",
		borderRadius: fullScreen ? 0 : "8px 0 0 8px",
		maxWidth: "100vw",
		padding: 0
	}
});

/**
 * ConversationLight component to display a conversation in a drawer.
 * @param {boolean} open - Whether the drawer is open.
 * @param {function} onClose - Function to close the drawer.
 * @param {Object} action - The action configuration.
 * @param {Object} values - The values related to the conversation.
 * @param {Object} resource - The resource configuration.
 * @return {JSX.Element} The rendered ConversationLight component.
 */
const ConversationLight = ({ open, onClose, action, values, resource }) => {
	const [conversation, setConversation] = useState({});
	const [tab, setTab] = useState(0);
	const [visualiseConversationLoading, setVisualiseConversationLoading] = useState(true);
	const [fullScreen, setFullScreen] = useState(false);
	const [dictionary, setDictionary] = useState({});
	const [dictionarySkeleton, setDictionarySkeleton] = useState({});

	const dispatch = useDispatch();

	const isConversationAnalysed =
		!lod_.isNil(conversation?.analyse) && !lod_.isEmpty(conversation?.analyse);

	/**
	 * Get conversation by ID.
	 * @param {string} id - The ID of the conversation to fetch.
	 */
	const getConversation = async id => {
		const queryConversation = await new Promise(resolve => {
			dispatch(
				FormAction.getItemsFromCollection(
					resource?.storage?.collection,
					{
						query: {
							_id: id
						}
					},
					res => {
						const item = res.items[0];
						resolve(item);
					}
				)
			);
		});

		setConversation(queryConversation);
		setVisualiseConversationLoading(false);
	};

	useEffect(() => {
		if (open === true) {
			setConversation({});
			setVisualiseConversationLoading(true);
			setTab(0);

			const id = values?._id;

			if (!lod_.isNil(id) && !lod_.isEmpty(id)) {
				getConversation(id);
			}
			// Reset dictionary when values change (new conversation)
			setDictionary({});
			setDictionarySkeleton({});
		} else {
			setConversation({});
			setVisualiseConversationLoading(true);
			setTab(0);
			setDictionary({});
			setDictionarySkeleton({});
		}
	}, [values, open]);

	useEffect(() => {
		if (open === true) {
			// Fetch dictionary when conversation and resource are ready
			const codeDictionary = resource?.format?.analyse;
			if (!lod_.isNil(codeDictionary) && !lod_.isEmpty(codeDictionary) && isConversationAnalysed) {
				(async () => {
					const result = await queryDictionary(dispatch, codeDictionary);
					if (!lod_.isNil(result)) {
						setDictionary(result);
						setDictionarySkeleton(createDictionarySkeleton(result, conversation?.analyse || {}));
					}
				})();
			}
		} else {
			setDictionary({});
			setDictionarySkeleton({});
		}
	}, [conversation, resource, isConversationAnalysed, dispatch, open]);

	return (
		<Drawer open={open} onClose={onClose} anchor="right" sx={drawerStyles(fullScreen)}>
			<Card sx={{ height: "100%", boxShadow: "none", borderRadius: 0, p: 0 }}>
				{visualiseConversationLoading ? (
					<MDBox
						style={{ height: "100%" }}
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						<CircularProgress color="info" size={60} />
					</MDBox>
				) : (
					<>
						<MDBox
							display="flex"
							alignItems="center"
							justifyContent="space-between"
							sx={{ px: 2, py: 1 }}
						>
							<MDBox display="flex" justifyContent="start" flexDirection="column">
								<MDTypography variant="h6">Conversation</MDTypography>
								<MDTypography variant="body2" fontSize="small">
									{conversation.fID}
								</MDTypography>
							</MDBox>
							<MDBox display="flex">
								{/* FullScreen button now acts on Drawer */}
								{!fullScreen ? (
									<Tooltip placement="top" title="Plein écran">
										<IconButton onClick={() => setFullScreen(true)}>
											<Icon>fullscreen</Icon>
										</IconButton>
									</Tooltip>
								) : (
									<Tooltip placement="top" title="Quitter le plein écran">
										<IconButton onClick={() => setFullScreen(false)}>
											<Icon>fullscreen_exit</Icon>
										</IconButton>
									</Tooltip>
								)}
								{/* Close */}
								<Tooltip placement="top" title="Fermer">
									<IconButton onClick={onClose}>
										<Icon>close</Icon>
									</IconButton>
								</Tooltip>
							</MDBox>
						</MDBox>
						{!fullScreen && (
							<MDBox sx={{ px: 2 }}>
								<Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="basic tabs example">
									<MDBox flex="1" onClick={() => setTab(0)}>
										<Tab style={{ width: "100%" }} label="Conversation" />
									</MDBox>
									<Tooltip
										title={!isConversationAnalysed ? "Vous devez d'abord analyser l'appel" : ""}
										placement="top"
									>
										<MDBox flex="1" onClick={() => (isConversationAnalysed ? setTab(1) : {})}>
											<Tab
												style={{ width: "100%" }}
												label="Analyse"
												disabled={!isConversationAnalysed}
											/>
										</MDBox>
									</Tooltip>
								</Tabs>
							</MDBox>
						)}
						{/* If we're not in fullscreen => Use tabs */}
						{!fullScreen && (
							<MDBox
								p={2}
								style={{
									overflowY: "auto"
								}}
							>
								{/* Conversation */}
								{tab === 0 && <ConversationTab conversation={conversation} resource={resource} />}
								{/* Analyse v3.0 */}
								{tab === 1 && isConversationAnalysed && (
									<Analyse
										conversation={conversation}
										resource={resource}
										dictionary={dictionary}
										dictionarySkeleton={dictionarySkeleton}
									/>
								)}
							</MDBox>
						)}
						{fullScreen && (
							<MDBox display="flex" style={{ height: "100%" }}>
								<MDBox flex={1} style={{ height: "100%", overflowY: "auto" }}>
									<ConversationTab conversation={conversation} resource={resource} />
								</MDBox>
							</MDBox>
						)}
					</>
				)}
			</Card>
		</Drawer>
	);
};

export default ConversationLight;
